/**
 * AlertsPanel - Panel centralizado para gestionar todas las alertas de precio
 * MEJORADO: Más grande, detallado, con controles de ventana y origen de alertas
 */
import { useState } from 'react';
import MarkedLevelsPanel from './MarkedLevelsPanel';
import { 
  Bell, BellRinging, X, TrendUp, TrendDown, Trash, Plus, 
  SpeakerHigh, SpeakerSlash, MagnifyingGlass, Funnel,
  ArrowsOut, ArrowsIn, Printer, Export, FloppyDisk, 
  Target, Lightning, ChartLineUp, Eye, Clock, CheckCircle,
  Info, CaretDown, CaretUp, Minus, PencilSimple, Check
} from '@phosphor-icons/react';
import { useAlerts } from '@/contexts/AlertsContext';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const AlertsPanel = ({ isOpen, onClose, onMinimize, markets = [], onTickerSelect }) => {
  const [activeTab, setActiveTab] = useState('price');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formTicker, setFormTicker] = useState('');
  const [formPrice, setFormPrice] = useState('');
  const [formCondition, setFormCondition] = useState('above');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCondition, setFilterCondition] = useState('all');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [editPrice, setEditPrice] = useState('');
  const [editCondition, setEditCondition] = useState('above');

  const { 
    alerts, 
    createAlert, 
    updateAlert,
    deleteAlert, 
    soundEnabled, 
    setSoundEnabled,
    testSound,
    getAlertProgress 
  } = useAlerts();

  if (!isOpen) return null;

  // Obtener precio actual de un ticker
  const getCurrentPrice = (ticker) => {
    const market = markets.find(m => m.ticker === ticker);
    return market?.priceEUR || 0;
  };

  // Identificar origen de la alerta
  const getAlertSource = (alert) => {
    // Si tiene source definido, usarlo directamente
    if (alert.source && typeof alert.source === 'object' && alert.source.name) {
      // Mapear el icono string a componente si es necesario
      const iconMap = {
        'Target': Target,
        'Bell': Bell,
        'ChartLineUp': ChartLineUp,
        'Lightning': Lightning
      };
      return {
        name: alert.source.name,
        icon: iconMap[alert.source.icon] || Bell,
        color: alert.source.color || 'amber'
      };
    }
    
    // Si source es un string
    if (alert.source && typeof alert.source === 'string') {
      if (alert.source === 'manual' || alert.source === 'Portafolio') {
        return { name: 'Portafolio', icon: Bell, color: 'amber' };
      }
      if (alert.source.includes('Estrategia') || alert.source.includes('compra')) {
        return { name: 'Estrategia de Compra', icon: Target, color: 'emerald' };
      }
    }
    
    // Si tiene createdFrom
    if (alert.createdFrom && typeof alert.createdFrom === 'object') {
      return alert.createdFrom;
    }
    
    // Fallback: Por defecto es Portafolio
    return { name: 'Portafolio', icon: Bell, color: 'amber' };
  };

  // Filtrar alertas
  const filteredAlerts = alerts.filter(alert => {
    const matchesSearch = searchTerm === '' || 
      alert.ticker.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterCondition === 'all' || alert.condition === filterCondition;
    return matchesSearch && matchesFilter;
  });

  // Ordenar alertas: primero las más cercanas a activarse
  const sortedAlerts = [...filteredAlerts].sort((a, b) => {
    const priceA = getCurrentPrice(a.ticker);
    const priceB = getCurrentPrice(b.ticker);
    const progressA = getAlertProgress(a, priceA);
    const progressB = getAlertProgress(b, priceB);
    return progressB - progressA;
  });

  const handleAddAlert = async (e) => {
    e.preventDefault();
    if (formTicker && formPrice) {
      setIsSaving(true);
      // Source: Portafolio (creada desde el panel de alertas)
      const source = {
        name: 'Portafolio',
        icon: 'Bell',
        color: 'amber'
      };
      await createAlert(formTicker, formPrice, formCondition, source);
      toast.success(
        <div className="flex flex-col">
          <span className="font-bold">Alerta creada</span>
          <span className="text-xs text-zinc-400">{formTicker} {formCondition === 'above' ? '↑' : '↓'} {formPrice}€</span>
        </div>
      );
      setFormTicker('');
      setFormPrice('');
      setShowAddForm(false);
      setTimeout(() => setIsSaving(false), 500);
    }
  };

  // Exportar a PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Mis Alertas de Precio', 14, 22);
    doc.setFontSize(10);
    doc.text(`Generado: ${new Date().toLocaleString('es-ES')}`, 14, 30);
    
    let y = 45;
    doc.setFontSize(12);
    sortedAlerts.forEach((alert, idx) => {
      const currentPrice = getCurrentPrice(alert.ticker);
      const progress = getAlertProgress(alert, currentPrice);
      doc.text(`${idx + 1}. ${alert.ticker} - ${alert.condition === 'above' ? 'Sube a' : 'Baja a'} ${alert.target_price.toFixed(2)}€ (${progress.toFixed(0)}%)`, 14, y);
      y += 8;
    });
    
    doc.save('mis_alertas.pdf');
    toast.success('PDF exportado correctamente');
  };

  // Imprimir - abre nueva ventana solo con el contenido del panel
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('No se pudo abrir ventana de impresión');
      return;
    }
    
    const alertsData = alerts.map(a => `
      <div style="border: 1px solid #333; padding: 15px; margin: 10px 0; border-radius: 8px;">
        <strong style="color: #22d3ee;">${a.ticker}</strong> - ${a.condition} ${a.target}€
        <br><small style="color: #888;">Estado: ${a.triggered ? 'Disparada' : 'Activa'}</small>
      </div>
    `).join('');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Mis Alertas - KAPITAL</title>
        <style>
          body { font-family: system-ui, -apple-system, sans-serif; padding: 20px; background: #0a0a0a; color: #e2e8f0; }
          h1 { color: #f59e0b; border-bottom: 2px solid #f59e0b; padding-bottom: 10px; }
        </style>
      </head>
      <body>
        <h1>🔔 Mis Alertas de Precio</h1>
        ${alertsData || '<p>No hay alertas configuradas</p>'}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
    toast.success('📄 Preparando impresión...');
  };

  // Sugerencias de tickers
  const tickerSuggestions = markets
    .filter(m => formTicker && m.ticker.toLowerCase().includes(formTicker.toLowerCase()))
    .slice(0, 5);

  // Tamaño de ventana
  const windowSize = isFullscreen 
    ? 'fixed inset-4 max-w-none max-h-none' 
    : 'w-full max-w-4xl max-h-[90vh]';

  // Si está minimizado externamente, no renderizar nada
  // (se mostrará en el Taskbar)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div 
        className={`bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-auto flex flex-col ${windowSize}`}
        style={{ 
          boxShadow: '0 0 60px rgba(255, 170, 0, 0.15), 0 20px 60px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-700 bg-gradient-to-r from-gold-600/20 to-orange-600/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-gradient-to-br from-gold-500 to-orange-600 rounded-xl shadow-lg">
                <BellRinging size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Centro de Alertas</h2>
                <p className="text-zinc-400 text-sm">
                  {alerts.filter(a => a.active !== false).length} alerta{alerts.filter(a => a.active !== false).length !== 1 ? 's' : ''} activa{alerts.filter(a => a.active !== false).length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            {/* Controles de ventana */}
            <div className="flex items-center gap-2">
              {/* Toggle sonido */}
              <button
                onClick={() => {
                  setSoundEnabled(!soundEnabled);
                  toast.success(soundEnabled ? 'Sonido desactivado' : 'Sonido activado');
                }}
                className={`p-2 rounded-lg transition-all ${
                  soundEnabled 
                    ? 'bg-green-600/30 text-green-400 hover:bg-green-600/50' 
                    : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'
                }`}
                title={soundEnabled ? 'Sonido activado' : 'Sonido desactivado'}
              >
                {soundEnabled ? <SpeakerHigh size={18} /> : <SpeakerSlash size={18} />}
              </button>
              
              {/* Imprimir */}
              <button
                onClick={handlePrint}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded-lg transition-all"
                title="Imprimir"
              >
                <Printer size={18} />
              </button>
              
              {/* Pantalla completa */}
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-400 hover:text-white rounded-lg transition-all"
                title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
              >
                {isFullscreen ? <ArrowsIn size={18} /> : <ArrowsOut size={18} />}
              </button>
              
              {/* Minimizar - Va al Taskbar */}
              <button
                onClick={() => {
                  if (onMinimize) onMinimize();
                }}
                className="p-2 bg-zinc-800 hover:bg-yellow-600 text-zinc-400 hover:text-white rounded-lg transition-all"
                title="Minimizar al Taskbar"
                data-testid="alerts-minimize-btn"
              >
                <Minus size={18} weight="bold" />
              </button>
              
              {/* Cerrar */}
              <button
                onClick={onClose}
                className="p-2 bg-zinc-800 hover:bg-red-600 text-zinc-400 hover:text-white rounded-lg transition-all"
                title="Cerrar"
              >
                <X size={18} weight="bold" />
              </button>
            </div>
          </div>
        </div>

        {/* Pestañas: Alerta de precio / Orden condicionada */}
        <div className="px-5 pt-3 flex gap-2 border-b border-zinc-700 bg-zinc-900/60">
          <button
            data-testid="tab-price-alerts"
            onClick={() => setActiveTab('price')}
            title="Alerta de precio: te avisa una vez cuando el activo alcanza el precio objetivo que definas (vigilada 24/7, llega a tu correo)"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-bold transition-all border-b-2 ${
              activeTab === 'price'
                ? 'text-gold-400 border-gold-500 bg-gold-500/10'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
          >
            <Bell size={15} weight="bold" />
            Alerta de precio
            <span className="hidden md:inline text-[10px] font-normal opacity-60">(Price alert)</span>
          </button>
          <button
            data-testid="tab-conditional-orders"
            onClick={() => setActiveTab('levels')}
            title="Orden condicionada (Stop loss / Take profit): niveles que marcas con un clic en el gráfico; te avisa cada vez que el precio los cruza (vigilada 24/7, llega a tu correo)"
            className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-bold transition-all border-b-2 ${
              activeTab === 'levels'
                ? 'text-zinc-400 border-zinc-500 bg-zinc-500/10'
                : 'text-zinc-500 border-transparent hover:text-zinc-300'
            }`}
          >
            <Target size={15} weight="bold" />
            Orden condicionada
            <span className="hidden md:inline text-[10px] font-normal opacity-60">(Stop loss / Take profit)</span>
          </button>
        </div>

        {activeTab === 'levels' && (
          <div className="flex-1 overflow-hidden min-h-[420px]">
            <MarkedLevelsPanel onTickerSelect={onTickerSelect} enrichedMarkets={markets} />
          </div>
        )}

        {activeTab === 'price' && (<>
        {/* Barra de herramientas */}
        <div className="px-3 md:px-5 py-3 border-b border-zinc-700 flex items-center gap-2 md:gap-3 flex-wrap bg-zinc-800/50">
          {/* Buscar */}
          <div className="relative flex-1 min-w-[140px] md:min-w-[180px]">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar ticker..."
              title="Filtra la lista de alertas por símbolo del activo"
              className="w-full pl-8 pr-3 py-2.5 md:py-2 bg-zinc-900 border border-zinc-600 rounded-lg text-white text-sm focus:border-gold-500 focus:outline-none min-h-[44px]"
            />
          </div>

          {/* Filtro condición */}
          <div className="flex gap-1">
            {[
              { value: 'all', label: 'Todas', color: 'amber' },
              { value: 'above', label: 'Sube', icon: TrendUp, color: 'green' },
              { value: 'below', label: 'Baja', icon: TrendDown, color: 'red' }
            ].map(filter => (
              <button
                key={filter.value}
                onClick={() => setFilterCondition(filter.value)}
                title={filter.value === 'all' ? 'Mostrar todas las alertas' : filter.value === 'above' ? 'Mostrar solo alertas de subida (se disparan al superar el precio)' : 'Mostrar solo alertas de bajada (se disparan al caer por debajo del precio)'}
                className={`flex items-center gap-1 px-2 md:px-3 py-2 rounded-lg text-xs font-bold transition-all min-h-[44px] ${
                  filterCondition === filter.value
                    ? `bg-${filter.color}-600/30 text-${filter.color}-400 border border-${filter.color}-500/50`
                    : 'bg-zinc-900 text-zinc-400 border border-zinc-700 hover:bg-zinc-700'
                }`}
              >
                {filter.icon && <filter.icon size={14} />}
                <span className="hidden sm:inline">{filter.label}</span>
              </button>
            ))}
          </div>

          {/* Botón añadir */}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            title={showAddForm ? 'Cerrar el formulario sin crear la alerta' : 'Crear una nueva alerta de precio: elige activo, condición (sube/baja) y precio objetivo'}
            className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg text-sm font-bold transition-all min-h-[44px] ${
              showAddForm
                ? 'bg-red-600 text-white'
                : 'bg-gradient-to-r from-gold-600 to-orange-600 text-white hover:from-gold-500 hover:to-orange-500'
            }`}
          >
            {showAddForm ? <X size={16} /> : <Plus size={16} />}
            <span className="hidden sm:inline">{showAddForm ? 'Cancelar' : 'Nueva Alerta'}</span>
          </button>
        </div>

        {/* Formulario de nueva alerta */}
        {showAddForm && (
          <div className="px-3 md:px-5 py-4 border-b border-gold-500/30 bg-gold-500/5">
            <form onSubmit={handleAddAlert} className="flex items-end gap-2 md:gap-3 flex-wrap">
              {/* Ticker */}
              <div className="relative flex-1 min-w-[120px] md:min-w-[140px]">
                <label className="text-xs text-zinc-500 mb-1 block">Ticker</label>
                <input
                  type="text"
                  value={formTicker}
                  onChange={(e) => setFormTicker(e.target.value.toUpperCase())}
                  placeholder="NVDA, AAPL..."
                  title="Escribe el símbolo del activo a vigilar (ej: NVDA, AAPL, BTC-USD)"
                  className="w-full px-3 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm font-mono focus:border-gold-500 focus:outline-none min-h-[44px]"
                  required
                />
                {tickerSuggestions.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl max-h-32 overflow-y-auto">
                    {tickerSuggestions.map(m => (
                      <button
                        key={m.ticker}
                        type="button"
                        onClick={() => {
                          setFormTicker(m.ticker);
                          setFormPrice((m.priceEUR * 1.05).toFixed(2));
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-700 flex justify-between min-h-[44px]"
                      >
                        <span className="font-mono font-bold text-white">{m.ticker}</span>
                        <span className="text-gold-400 font-bold">{m.priceEUR?.toFixed(2)}€</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Condición */}
              <div className="w-[100px] md:w-[120px]">
                <label className="text-xs text-zinc-500 mb-1 block">Condición</label>
                <select
                  value={formCondition}
                  onChange={(e) => setFormCondition(e.target.value)}
                  title="Sube: se dispara al alcanzar o superar el precio objetivo · Baja: se dispara al caer hasta ese precio o por debajo"
                  className="w-full px-2 md:px-3 py-2.5 bg-zinc-800 border border-zinc-600 rounded-lg text-white text-sm focus:border-gold-500 focus:outline-none min-h-[44px]"
                >
                  <option value="above">Sube ↑</option>
                  <option value="below">Baja ↓</option>
                </select>
              </div>

              {/* Precio */}
              <div className="w-[100px] md:w-[130px]">
                <label className="text-xs text-gold-400 mb-1 block font-bold">Precio €</label>
                <div className="relative">
                  <span className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 text-gold-400 font-bold">€</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formPrice}
                    onChange={(e) => setFormPrice(e.target.value)}
                    placeholder="0.00"
                    title="Precio objetivo en euros al que se disparará la alerta"
                    className="w-full pl-6 md:pl-7 pr-2 md:pr-3 py-2.5 bg-zinc-800 border border-gold-600/50 rounded-lg text-white text-sm font-mono focus:border-gold-500 focus:outline-none min-h-[44px]"
                    required
                  />
                </div>
              </div>

              {/* Botón crear */}
              <button
                type="submit"
                disabled={isSaving}
                title="Guardar la alerta: se vigilará 24/7 y recibirás un email al cumplirse"
                className={`px-5 py-2.5 bg-gradient-to-r from-gold-600 to-orange-600 text-white text-sm font-bold rounded-lg hover:from-gold-500 hover:to-orange-500 transition-all flex items-center gap-2 ${
                  isSaving ? 'opacity-70 cursor-wait' : ''
                }`}
              >
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Bell size={16} />
                    Crear Alerta
                  </>
                )}
              </button>
            </form>
          </div>
        )}

        {/* Contenido principal */}
        <div className="flex-1 overflow-auto flex">
          {/* Lista de alertas */}
          <div className={`flex-1 overflow-y-auto p-5 ${selectedAlert ? 'border-r border-zinc-700' : ''}`}>
            {sortedAlerts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-zinc-800 flex items-center justify-center">
                  <Bell size={40} className="text-zinc-600" />
                </div>
                <div className="text-zinc-400 text-lg font-bold">No hay alertas configuradas</div>
                <div className="text-zinc-600 text-sm mt-2 max-w-xs mx-auto">
                  Crea alertas para recibir notificaciones cuando un activo alcance un precio objetivo
                </div>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="mt-6 px-6 py-3 bg-gradient-to-r from-gold-600 to-orange-600 text-white rounded-xl font-bold hover:from-gold-500 hover:to-orange-500 transition-all"
                >
                  <Plus size={18} className="inline mr-2" />
                  Crear primera alerta
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {sortedAlerts.map((alert, idx) => {
                  const currentPrice = getCurrentPrice(alert.ticker);
                  const progress = getAlertProgress(alert, currentPrice);
                  const isTriggered = progress >= 100;
                  const diff = alert.condition === 'above'
                    ? alert.target_price - currentPrice
                    : currentPrice - alert.target_price;
                  const source = getAlertSource(alert);
                  const alertKey = alert.id || `${alert.ticker}-${alert.condition}-${alert.target_price}-${idx}`;
                  const isSelected = selectedAlert && (
                    selectedAlert.id === alert.id || 
                    (selectedAlert.ticker === alert.ticker && 
                     parseFloat(selectedAlert.target_price) === parseFloat(alert.target_price))
                  );

                  return (
                    <div
                      key={alertKey}
                      onClick={() => {
                        setSelectedAlert(isSelected ? null : alert);
                      }}
                      className={`relative p-4 rounded-xl border transition-all cursor-pointer ${
                        isTriggered
                          ? 'bg-green-500/10 border-green-500/50'
                          : isSelected
                            ? 'bg-gold-500/10 border-gold-500/50 ring-2 ring-gold-500/30'
                            : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-500 hover:bg-zinc-800'
                      }`}
                    >
                      {/* Barra de progreso */}
                      {!isTriggered && progress > 0 && (
                        <div
                          className={`absolute bottom-0 left-0 h-1 rounded-b-xl transition-all ${
                            alert.condition === 'above' ? 'bg-green-500/50' : 'bg-red-500/50'
                          }`}
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        />
                      )}

                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 flex-1">
                          {/* Icono condición */}
                          <div className={`p-2.5 rounded-xl ${
                            alert.condition === 'above' 
                              ? 'bg-green-600/20 text-green-400' 
                              : 'bg-red-600/20 text-red-400'
                          }`}>
                            {alert.condition === 'above' ? (
                              <TrendUp size={22} weight="bold" />
                            ) : (
                              <TrendDown size={22} weight="bold" />
                            )}
                          </div>

                          {/* Info principal */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono font-bold text-white text-lg">{alert.ticker}</span>
                              {isTriggered && (
                                <span className="text-[10px] bg-green-500 text-black px-2 py-0.5 rounded-full font-bold animate-pulse">
                                  ACTIVADA
                                </span>
                              )}
                              {/* Badge de origen */}
                              <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold bg-${source.color}-500/20 text-${source.color}-400 border border-${source.color}-500/30`}>
                                {source.name}
                              </span>
                            </div>
                            <div className="text-sm text-zinc-400 mt-0.5">
                              {alert.condition === 'above' ? 'Sube a' : 'Baja a'}{' '}
                              <span className="font-mono font-bold text-white">
                                {parseFloat(alert.target_price).toFixed(2)}€
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          {/* Precio actual y diferencia */}
                          {currentPrice > 0 && (
                            <div className="text-right">
                              <div className="text-[10px] text-gold-400 font-bold uppercase">Actual</div>
                              <div className="font-mono text-white font-bold">{currentPrice.toFixed(2)}€</div>
                              {!isTriggered && (
                                <div className={`text-xs font-mono ${
                                  alert.condition === 'above' ? 'text-green-400' : 'text-red-400'
                                }`}>
                                  {diff > 0 ? '+' : ''}{diff.toFixed(2)} ({progress.toFixed(0)}%)
                                </div>
                              )}
                            </div>
                          )}

                          {/* Botones de acción */}
                          <div className="flex items-center gap-1">
                            {/* Botón editar */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingAlert(alert);
                                setEditPrice(alert.target_price.toString());
                                setEditCondition(alert.condition);
                              }}
                              className="p-2 text-zinc-500 hover:text-gold-400 hover:bg-gold-500/10 rounded-lg transition-all"
                              title="Editar alerta"
                            >
                              <PencilSimple size={18} />
                            </button>
                            
                            {/* Botón eliminar */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteAlert(alert.id);
                                toast.success('Alerta eliminada');
                                if (isSelected) setSelectedAlert(null);
                              }}
                              className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                              title="Eliminar alerta"
                            >
                              <Trash size={18} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Panel de detalle de alerta seleccionada */}
          {selectedAlert && (
            <div className="w-80 p-5 bg-zinc-800/30 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white">Detalle de Alerta</h3>
                <button
                  onClick={() => setSelectedAlert(null)}
                  className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors"
                >
                  <X size={16} className="text-zinc-400" />
                </button>
              </div>

              {/* Ticker grande */}
              <div className="text-center mb-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-3 ${
                  selectedAlert.condition === 'above' 
                    ? 'bg-green-600/20' 
                    : 'bg-red-600/20'
                }`}>
                  {selectedAlert.condition === 'above' ? (
                    <TrendUp size={36} className="text-green-400" weight="bold" />
                  ) : (
                    <TrendDown size={36} className="text-red-400" weight="bold" />
                  )}
                </div>
                <div className="text-2xl font-mono font-bold text-white">{selectedAlert.ticker}</div>
                <div className={`text-sm ${selectedAlert.condition === 'above' ? 'text-green-400' : 'text-red-400'}`}>
                  {selectedAlert.condition === 'above' ? 'Alerta de Subida' : 'Alerta de Bajada'}
                </div>
              </div>

              {/* Detalles */}
              <div className="space-y-4">
                <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-700">
                  <div className="text-[10px] text-zinc-500 uppercase font-bold mb-1">Precio Objetivo</div>
                  <div className="text-xl font-mono font-bold text-gold-400">
                    {parseFloat(selectedAlert.target_price).toFixed(2)}€
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-700">
                  <div className="text-[10px] text-zinc-500 uppercase font-bold mb-1">Precio Actual</div>
                  <div className="text-xl font-mono font-bold text-white">
                    {getCurrentPrice(selectedAlert.ticker).toFixed(2)}€
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-700">
                  <div className="text-[10px] text-zinc-500 uppercase font-bold mb-1">Progreso</div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          selectedAlert.condition === 'above' ? 'bg-green-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(getAlertProgress(selectedAlert, getCurrentPrice(selectedAlert.ticker)), 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-mono font-bold text-white">
                      {getAlertProgress(selectedAlert, getCurrentPrice(selectedAlert.ticker)).toFixed(0)}%
                    </span>
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-700">
                  <div className="text-[10px] text-zinc-500 uppercase font-bold mb-1">Origen</div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const source = getAlertSource(selectedAlert);
                      const Icon = source.icon;
                      return (
                        <>
                          <Icon size={18} className={`text-${source.color}-400`} />
                          <span className="text-white font-bold">{source.name}</span>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="p-3 rounded-xl bg-zinc-900/50 border border-zinc-700">
                  <div className="text-[10px] text-zinc-500 uppercase font-bold mb-1">Creada</div>
                  <div className="flex items-center gap-2 text-white">
                    <Clock size={16} className="text-zinc-500" />
                    {selectedAlert.created_at 
                      ? new Date(selectedAlert.created_at).toLocaleString('es-ES')
                      : 'Fecha no disponible'
                    }
                  </div>
                </div>

                {/* Botón eliminar grande */}
                <button
                  onClick={() => {
                    deleteAlert(selectedAlert.id);
                    setSelectedAlert(null);
                    toast.success('Alerta eliminada');
                  }}
                  className="w-full py-3 rounded-xl bg-red-600/20 text-red-400 font-bold hover:bg-red-600/30 transition-all flex items-center justify-center gap-2"
                >
                  <Trash size={18} />
                  Eliminar Alerta
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-zinc-700 bg-zinc-800/50 flex items-center justify-between">
          <p className="text-xs text-zinc-500">
            Las alertas se vigilan 24/7 en el servidor y llegan a tu correo de sesión
          </p>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <CheckCircle size={14} className="text-green-500" />
            Sistema activo
          </div>
        </div>
        </>)}
      </div>

      {/* Modal de edición de alerta */}
      {editingAlert && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setEditingAlert(null)}
        >
          <div 
            className="bg-zinc-900 border border-gold-500/50 rounded-2xl shadow-2xl p-6 w-full max-w-md"
            style={{ boxShadow: '0 0 40px rgba(0, 255, 255, 0.15)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-gradient-to-br from-gold-500 to-zinc-600 rounded-xl">
                  <PencilSimple size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Editar Alerta</h3>
                  <p className="text-sm text-zinc-400 font-mono">{editingAlert.ticker}</p>
                </div>
              </div>
              <button
                onClick={() => setEditingAlert(null)}
                className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
              >
                <X size={20} className="text-zinc-400" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Condición */}
              <div>
                <label className="text-xs text-zinc-500 uppercase font-bold mb-2 block">Condición</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditCondition('above')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition-all ${
                      editCondition === 'above'
                        ? 'bg-green-600/30 text-green-400 border-2 border-green-500'
                        : 'bg-zinc-800 text-zinc-400 border-2 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <TrendUp size={18} weight="bold" />
                    Sube a
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditCondition('below')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold transition-all ${
                      editCondition === 'below'
                        ? 'bg-red-600/30 text-red-400 border-2 border-red-500'
                        : 'bg-zinc-800 text-zinc-400 border-2 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <TrendDown size={18} weight="bold" />
                    Baja a
                  </button>
                </div>
              </div>

              {/* Precio objetivo */}
              <div>
                <label className="text-xs text-gold-400 uppercase font-bold mb-2 block">Precio Objetivo (€)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gold-400 font-bold text-lg">€</span>
                  <input
                    type="number"
                    step="0.01"
                    value={editPrice}
                    onChange={(e) => setEditPrice(e.target.value)}
                    className="w-full pl-10 pr-4 py-4 bg-zinc-800 border-2 border-gold-600/50 rounded-xl text-white text-xl font-mono font-bold focus:border-gold-400 focus:outline-none"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Precio actual: <span className="font-mono text-white">{getCurrentPrice(editingAlert.ticker).toFixed(2)}€</span>
                </p>
              </div>

              {/* Botones */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setEditingAlert(null)}
                  className="flex-1 px-4 py-3 bg-zinc-800 text-zinc-300 rounded-xl font-bold hover:bg-zinc-700 transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if (!editPrice || parseFloat(editPrice) <= 0) {
                      toast.error('Introduce un precio válido');
                      return;
                    }
                    const success = await updateAlert(editingAlert.id, {
                      target_price: parseFloat(editPrice),
                      condition: editCondition
                    });
                    if (success) {
                      setEditingAlert(null);
                      // Actualizar selectedAlert si es la misma
                      if (selectedAlert && selectedAlert.id === editingAlert.id) {
                        setSelectedAlert({
                          ...selectedAlert,
                          target_price: parseFloat(editPrice),
                          condition: editCondition
                        });
                      }
                    }
                  }}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-gold-600 to-zinc-600 text-white rounded-xl font-bold hover:from-gold-500 hover:to-zinc-500 transition-all flex items-center justify-center gap-2"
                >
                  <Check size={18} weight="bold" />
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;
